import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { useClientActionHistos } from '@/hooks/client';
import { ClientActionHistoModal } from '@/types/modal';
import { NoFooterModal } from '@/components/molecules/common/Modal';
import Table from '@/components/molecules/common/Table';
import { transformDateStringFrance } from '@/utilities/datetime';
import useTable, { DEFAULT_PAGE_SIZE } from '@/hooks/table';

const View: React.FC<{ modal: ClientActionHistoModal }> = ({ modal }) => {
  const { t } = useTranslation();

  const { page, pageSize, totalItems, onPageChange, persistTotalItems } = useTable();

  const { client, onClose } = modal;

  const { data: histos, isFetching } = useClientActionHistos(Number(client.clientId), page, pageSize);

  useEffect(() => {
    if (histos) persistTotalItems(histos.total);
  }, [histos]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case '1':
        return '#198754';
      case '2':
        return '#dc3545';
      default:
        return '';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case '1':
        return t('modal.client_action_histo.status_success');
      case '2':
        return t('modal.client_action_histo.status_fail');
      default:
        return '-';
    }
  };

  const columns: GridColDef[] = [
    { field: 'action', headerName: t('modal.client_action_histo.action') },
    {
      field: 'comment',
      headerName: t('modal.client_action_histo.comment'),
      renderCell: (params: GridCellParams) => <div dangerouslySetInnerHTML={{ __html: params.value || '' }} />,
    },
    { field: 'superuser', headerName: t('modal.client_action_histo.superuser') },
    { field: 'date', headerName: t('modal.client_action_histo.date') },
    {
      field: 'status',
      headerName: t('modal.client_action_histo.status'),
      renderCell: (params: GridCellParams) => (
        <div style={{ color: getStatusColor(String(params.value)) }}> {getStatusText(String(params.value))} </div>
      ),
    },
  ];

  const rows = histos
    ? histos.data.map((item, index) => {
        let comment = item.comment;
        if (!comment) {
          comment = t('modal.client_action_histo.no_comment');
        }
        const date = transformDateStringFrance(item.date);
        return { ...item, date: date || '', comment: comment, id: index };
      })
    : [];

  const body = (
    <Table
      rows={rows}
      columns={columns}
      loading={isFetching}
      table={{
        kind: 'DynamicTable',
        totalItem: totalItems,
        pageSize,
        pageSizeOptions: [5, 50, DEFAULT_PAGE_SIZE],
        onPageChange,
      }}
      sx={{
        '& .MuiDataGrid-cell': {
          display: 'flex !important',
          padding: '0.6rem 1rem !important',
          alignItems: 'center !important',
        },
      }}
    />
  );

  return (
    <NoFooterModal
      title={t('modal.client_action_histo.title')}
      onClose={onClose}
      body={body}
      modalName="client_action_histo"
      containerStyle={{ width: 'calc(100% - 4rem)' }}
    />
  );
};

export default View;
